/**
 * Drizzle query builders (over local PowerSync/SQLite) for the WORKFORCE domain.
 *
 * The local mirror synchronizes `employee_profile` + `usage_event` +
 * `proposed_time_entry` (`my_workforce`/`org_workforce` streams). The MANAGER reads
 * their dashboard from here; the EMPLOYEE themselves reads their profile (transparency) and their
 * proposed entries. Without filter by user_id: local DB only contains what
 * sync rules already authorize (my rows + rows of my organizations).
 *
 * Minimization (§2e): these queries NEVER read content — only usage
 * category, active seconds, and entry metadata.
 */

import { and, asc, count, desc, eq, isNull, sql } from "drizzle-orm";
import {
  employee_profile,
  type PowerSyncDrizzleDB,
  proposed_time_entry,
  usage_event,
} from "@jornada/powersync";

// ─── Employee profiles ───────────────────────────────────────────────────

/** LIVE (not deleted) and non-archived profiles, alphabetical. */
export function getEmployeeProfilesQuery(
  db: PowerSyncDrizzleDB,
  options?: { includeArchived?: boolean },
) {
  const base = db.select().from(employee_profile).where(isNull(employee_profile.deleted_at));
  const query = options?.includeArchived
    ? base
    : db
        .select()
        .from(employee_profile)
        .where(
          and(
            isNull(employee_profile.deleted_at),
            sql`COALESCE(${employee_profile.archived}, 0) = 0`,
          ),
        );
  return query.orderBy(asc(employee_profile.name));
}

/** A specific profile (detail / transparency). */
export function getEmployeeProfileByIdQuery(db: PowerSyncDrizzleDB, id: string) {
  return db
    .select()
    .from(employee_profile)
    .where(and(eq(employee_profile.id, id), isNull(employee_profile.deleted_at)));
}

/**
 * Number of LIVE profiles for a user. Used by the login flow
 * (`checkUnsyncedActor`) to decide if a guest has local data to
 * offer merging (keep/discard) when migrating to a real account.
 */
export function getEmployeeProfileCountForUserQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({ total: count() })
    .from(employee_profile)
    .where(and(eq(employee_profile.user_id, userId), isNull(employee_profile.deleted_at)));
}

// ─── Usage telemetry (aggregated, NEVER content) ──────────────────────────

/** Usage events of a profile (raw, for debugging/export). */
export function getUsageEventsByProfileQuery(db: PowerSyncDrizzleDB, profileId: string) {
  return db
    .select()
    .from(usage_event)
    .where(eq(usage_event.profile_id, profileId))
    .orderBy(desc(usage_event.started_at));
}

/**
 * AGGREGATED active seconds by category across the ENTIRE team (no profile
 * filter) — feeds the overview productivity ratio. Category only.
 */
export function getTeamUsageByCategoryQuery(db: PowerSyncDrizzleDB) {
  return db
    .select({
      category: usage_event.category,
      total_secs: sql<number>`COALESCE(SUM(${usage_event.active_secs}), 0)`.as("total_secs"),
    })
    .from(usage_event)
    .groupBy(usage_event.category);
}

/**
 * AGGREGATED active seconds by category of a profile ({ category, total_secs }).
 * Base of the dashboard's productivity summary (crossed with the profile's catalog
 * via `classifyCategoryTotals`). Category only — never app/title.
 */
export function getUsageByCategoryQuery(db: PowerSyncDrizzleDB, profileId: string) {
  return db
    .select({
      category: usage_event.category,
      total_secs: sql<number>`COALESCE(SUM(${usage_event.active_secs}), 0)`.as("total_secs"),
    })
    .from(usage_event)
    .where(eq(usage_event.profile_id, profileId))
    .groupBy(usage_event.category);
}

/**
 * Minimized telemetry (category + active seconds + start, NEVER content)
 * feeding opt-in PERSONAL productivity gamification. The local DB only
 * contains what sync rules already authorize (CC-40), so the game state
 * is personal by construction — never comparative among employees.
 */
export function getGamificationUsageEventsQuery(
  db: PowerSyncDrizzleDB,
  options?: { limit?: number },
) {
  return db
    .select({
      id: usage_event.id,
      category: usage_event.category,
      active_secs: usage_event.active_secs,
      started_at: usage_event.started_at,
    })
    .from(usage_event)
    .orderBy(asc(usage_event.started_at))
    .limit(options?.limit ?? 1000);
}

// ─── PROPOSED time entries ─────────────────────────────────────────────

/** Entries of a profile (review inbox), most recent first. */
export function getProposedEntriesByProfileQuery(
  db: PowerSyncDrizzleDB,
  profileId: string,
  options?: { status?: string },
) {
  const conditions = [eq(proposed_time_entry.profile_id, profileId)];
  if (options?.status) {
    conditions.push(eq(proposed_time_entry.status, options.status));
  }
  return db
    .select()
    .from(proposed_time_entry)
    .where(and(...conditions))
    .orderBy(desc(proposed_time_entry.start_time));
}

/** Entries of an employee (subject) by status — employee's own inbox. */
export function getProposedEntriesBySubjectQuery(
  db: PowerSyncDrizzleDB,
  subjectId: string,
  options?: { status?: string },
) {
  const conditions = [eq(proposed_time_entry.subject_id, subjectId)];
  if (options?.status) {
    conditions.push(eq(proposed_time_entry.status, options.status));
  }
  return db
    .select()
    .from(proposed_time_entry)
    .where(and(...conditions))
    .orderBy(desc(proposed_time_entry.start_time));
}

/** A specific entry (detail / read-before-write in mutations). */
export function getProposedEntryByIdQuery(db: PowerSyncDrizzleDB, id: string) {
  return db.select().from(proposed_time_entry).where(eq(proposed_time_entry.id, id));
}

/**
 * Number of PENDING review entries (proposed + edited) — feeds the overview
 * badge. Does not count already approved/rejected entries.
 */
export function getPendingProposedCountQuery(db: PowerSyncDrizzleDB) {
  return db
    .select({ total: count() })
    .from(proposed_time_entry)
    .where(sql`${proposed_time_entry.status} IN ('proposed', 'edited')`);
}

/**
 * Project attribution of a profile: seconds per project of NON-rejected
 * entries ({ project_id, project_code, project_name, total_secs }). Time
 * without a project is grouped under project_id NULL (for the employee to assign).
 */
export function getProjectAttributionByProfileQuery(db: PowerSyncDrizzleDB, profileId: string) {
  return db
    .select({
      project_id: proposed_time_entry.project_id,
      project_code: proposed_time_entry.project_code,
      project_name: proposed_time_entry.project_name,
      total_secs: sql<number>`COALESCE(SUM(${proposed_time_entry.duration_secs}), 0)`.as(
        "total_secs",
      ),
    })
    .from(proposed_time_entry)
    .where(
      and(
        eq(proposed_time_entry.profile_id, profileId),
        sql`COALESCE(${proposed_time_entry.status}, 'proposed') <> 'rejected'`,
      ),
    )
    .groupBy(
      proposed_time_entry.project_id,
      proposed_time_entry.project_code,
      proposed_time_entry.project_name,
    );
}

// ─── Transparency (§2e): what the EMPLOYEE sees of their own profile ──────────

/**
 * Transparency read of a profile: its disclosure (what is collected),
 * registered consent, and work schedule. Source of the §2e page.
 */
export function getTransparencyQuery(db: PowerSyncDrizzleDB, profileId: string) {
  return db
    .select({
      id: employee_profile.id,
      name: employee_profile.name,
      device_label: employee_profile.device_label,
      legal_basis: employee_profile.legal_basis,
      disclosure: employee_profile.disclosure,
      consent: employee_profile.consent,
      work_schedule: employee_profile.work_schedule,
    })
    .from(employee_profile)
    .where(and(eq(employee_profile.id, profileId), isNull(employee_profile.deleted_at)));
}
